import { useState, useEffect, useCallback, useRef } from 'react'
import {
  X,
  MessageCircle,
  Sparkles,
  Brain,
  FlaskConical,
  Trash2,
  CheckSquare,
  Square,
  MinusSquare,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'
import { aiRepo, authRepo } from '@/lib/repositories'
import { CONVERSATION_MESSAGE_LIMIT } from '@/lib/repositories'
import type { ConversationSummary } from '@/lib/repositories'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

interface ConversationHistorySheetProps {
  open: boolean
  onClose: () => void
  onSelectConversation: (conversation: ConversationSummary) => void
  isAdmin?: boolean
}

export function ConversationHistorySheet({
  open,
  onClose,
  onSelectConversation,
  isAdmin = false,
}: ConversationHistorySheetProps) {
  const { t, i18n } = useTranslation()
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [viewType, setViewType] = useState<'regular' | 'sandbox'>('regular')

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isSelecting, setIsSelecting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Confirm dialog state
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'single' | 'bulk'
    id?: string
  } | null>(null)

  // Guard against concurrent loads
  const loadingRef = useRef(false)

  function formatDate(dateStr: string): string {
    const d = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffDays = Math.floor(diffMs / 86400000)
    const locale = i18n.language === 'zh' ? 'zh-CN' : i18n.language

    if (diffDays === 0) return t('today')
    if (diffDays === 1) return t('yesterday')
    if (diffDays < 7) return t('daysAgo', { count: diffDays })
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  }

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setViewType('regular')
      setSelectedIds(new Set())
      setIsSelecting(false)
    }
  }, [open])

  const loadConversations = useCallback(
    async (type: 'regular' | 'sandbox') => {
      // Prevent concurrent loads
      if (loadingRef.current) return
      loadingRef.current = true
      setIsLoading(true)

      try {
        const user = await authRepo.getCurrentUser()
        if (!user) return
        const convos = await aiRepo.listConversations(user.id, type)
        setConversations(convos)
      } catch (err) {
        console.error('Failed to load conversations:', err)
      } finally {
        setIsLoading(false)
        loadingRef.current = false
      }
    },
    []
  )

  useEffect(() => {
    if (!open) return
    loadConversations(viewType)
  }, [open, viewType, loadConversations])

  const handleToggleView = (type: 'regular' | 'sandbox') => {
    if (type === viewType) return
    setSelectedIds(new Set())
    setIsSelecting(false)
    setViewType(type)
  }

  // Selection handlers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.id)))
    }
  }

  const exitSelectMode = () => {
    setIsSelecting(false)
    setSelectedIds(new Set())
  }

  // Delete handlers
  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return
    setConfirmDelete({ type: 'bulk' })
  }

  const handleDeleteSingle = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirmDelete({ type: 'single', id })
  }

  const executeDelete = async () => {
    if (!confirmDelete) return

    setIsDeleting(true)
    try {
      if (confirmDelete.type === 'bulk') {
        const count = selectedIds.size
        await aiRepo.deleteConversations(Array.from(selectedIds))
        setConversations((prev) =>
          prev.filter((c) => !selectedIds.has(c.id))
        )
        setSelectedIds(new Set())
        setIsSelecting(false)
        toast.success(
          `Deleted ${count} conversation${count > 1 ? 's' : ''}`
        )
      } else if (confirmDelete.id) {
        await aiRepo.deleteConversation(confirmDelete.id)
        setConversations((prev) =>
          prev.filter((c) => c.id !== confirmDelete.id)
        )
        selectedIds.delete(confirmDelete.id)
        setSelectedIds(new Set(selectedIds))
        toast.success('Conversation deleted')
      }
    } catch (err) {
      console.error('Failed to delete:', err)
      toast.error('Failed to delete conversation(s)')
    } finally {
      setIsDeleting(false)
      setConfirmDelete(null)
    }
  }

  const userMessageCount = (total: number) => Math.ceil(total / 2)

  const isSandboxView = viewType === 'sandbox'
  const allSelected =
    conversations.length > 0 && selectedIds.size === conversations.length
  const someSelected = selectedIds.size > 0 && !allSelected

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-[98vh] max-h-[98vh] w-full sm:max-w-md p-0">
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-11 h-11 rounded-full flex items-center justify-center shadow-lg',
                isSandboxView
                  ? 'bg-gradient-to-br from-violet-500 to-pink-500 shadow-violet-500/20'
                  : 'bg-gradient-to-br from-pink-500 to-fuchsia-500 shadow-pink-500/20'
              )}
            >
              {isSandboxView ? (
                <FlaskConical className="w-5 h-5 text-white" />
              ) : (
                <Brain className="w-5 h-5 text-black" />
              )}
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-white">
                {isSandboxView
                  ? 'Sandbox History'
                  : t('conversationHistory')}
              </SheetTitle>
              <p className="text-xs text-neutral-400 mt-0.5">
                {t('conversationCount', { count: conversations.length })}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Admin-only type toggle */}
        {isAdmin && (
          <div className="flex-shrink-0 px-5 pt-4 pb-2">
            <div className="flex rounded-lg overflow-hidden border border-white/[0.08]">
              <button
                type="button"
                onClick={() => handleToggleView('regular')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
                  viewType === 'regular'
                    ? 'bg-pink-500/20 text-pink-300 border-r border-pink-500/30'
                    : 'bg-white/[0.02] text-neutral-400 border-r border-white/[0.08] hover:bg-white/[0.04]'
                )}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Chats
              </button>
              <button
                type="button"
                onClick={() => handleToggleView('sandbox')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
                  viewType === 'sandbox'
                    ? 'bg-violet-500/20 text-violet-300'
                    : 'bg-white/[0.02] text-neutral-400 hover:bg-white/[0.04]'
                )}
              >
                <FlaskConical className="w-3.5 h-3.5" />
                Sandbox
              </button>
            </div>
          </div>
        )}

        {/* Selection toolbar */}
        {conversations.length > 0 && !isLoading && (
          <div className="flex-shrink-0 flex items-center justify-between px-5 py-2 border-b border-white/[0.04]">
            {isSelecting ? (
              <>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition-colors"
                  >
                    {allSelected ? (
                      <CheckSquare className="w-4 h-4 text-red-400" />
                    ) : someSelected ? (
                      <MinusSquare className="w-4 h-4 text-red-400" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedIds.size > 0 && (
                    <span className="text-[10px] text-neutral-500 font-mono">
                      {selectedIds.size} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {selectedIds.size > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteSelected}
                      disabled={isDeleting}
                      className="h-7 px-2.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete ({selectedIds.size})
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={exitSelectMode}
                    className="h-7 px-2.5 text-xs text-neutral-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <span className="text-[10px] text-neutral-600">
                  {conversations.length} conversation
                  {conversations.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setIsSelecting(true)}
                  className="text-[10px] text-neutral-500 hover:text-neutral-300 transition-colors"
                >
                  Select
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <div
                  className={cn(
                    'w-4 h-4 border-2 border-neutral-600 rounded-full animate-spin',
                    isSandboxView
                      ? 'border-t-violet-400'
                      : 'border-t-pink-400'
                  )}
                />
                {t('loadingConversations')}
              </div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-neutral-900/50 border border-white/[0.06] flex items-center justify-center mb-4">
                {isSandboxView ? (
                  <FlaskConical className="w-7 h-7 text-neutral-600" />
                ) : (
                  <MessageCircle className="w-7 h-7 text-neutral-600" />
                )}
              </div>
              <p className="text-neutral-400 text-sm">
                {isSandboxView
                  ? 'No sandbox conversations yet'
                  : t('noConversationsYet')}
              </p>
              <p className="text-neutral-600 text-xs mt-1">
                {isSandboxView
                  ? 'Start a conversation in the AI Sandbox'
                  : t('startChattingHistory')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {conversations.map((conv) => {
                const userMsgs = userMessageCount(conv.messageCount)
                const isAtLimit = userMsgs >= CONVERSATION_MESSAGE_LIMIT
                const isNearLimit = userMsgs >= CONVERSATION_MESSAGE_LIMIT - 5
                const isSandboxConv = conv.conversationType === 'sandbox'
                const isSelected = selectedIds.has(conv.id)

                return (
                  <div
                    key={conv.id}
                    className={cn(
                      'w-full flex items-center overflow-hidden hover:bg-white/[0.02] transition-colors text-left',
                      isSelected && 'bg-white/[0.03]'
                    )}
                  >
                    {/* Checkbox (select mode) */}
                    {isSelecting && (
                      <button
                        onClick={() => toggleSelect(conv.id)}
                        className="flex-shrink-0 pl-5 self-center"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-red-400" />
                        ) : (
                          <Square className="w-4 h-4 text-neutral-600" />
                        )}
                      </button>
                    )}

                    {/* Main content — clickable to open conversation */}
                    <button
                      onClick={() => {
                        if (isSelecting) {
                          toggleSelect(conv.id)
                          return
                        }
                        onClose()
                        setTimeout(() => onSelectConversation(conv), 150)
                      }}
                      className={cn(
                        'flex-1 min-w-0 flex items-start gap-3 py-4 text-left',
                        isSelecting ? 'pl-3 pr-2' : 'px-5'
                      )}
                    >
                      <div
                        className={cn(
                          'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                          isSandboxConv
                            ? 'bg-gradient-to-br from-violet-500 to-pink-500'
                            : conv.conversationType === 'general'
                              ? 'bg-gradient-to-br from-emerald-500 to-cyan-500'
                              : 'bg-gradient-to-br from-cyan-500 to-blue-500'
                        )}
                      >
                        {isSandboxConv ? (
                          <FlaskConical className="w-4 h-4 text-white" />
                        ) : (
                          <Sparkles className="w-4 h-4 text-black" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium truncate">
                            {isSandboxConv
                              ? 'Sandbox Chat'
                              : conv.conversationType === 'general'
                                ? t('generalChat')
                                : conv.ticker
                                  ? `$${conv.ticker}`
                                  : t('insightChat')}
                          </span>
                          <span className="text-neutral-600 text-xs flex-shrink-0 whitespace-nowrap">
                            {formatDate(conv.updatedAt)}
                          </span>
                        </div>
                        {conv.lastMessage && (
                          <p className="text-neutral-500 text-xs mt-0.5 truncate">
                            {conv.lastMessage}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-neutral-600 text-[10px]">
                            {isSandboxConv
                              ? `${conv.messageCount} messages`
                              : t('messagesCount', {
                                  count: userMsgs,
                                  limit: CONVERSATION_MESSAGE_LIMIT,
                                })}
                          </span>
                          {!isSandboxConv && isAtLimit && (
                            <span className="px-1.5 py-0.5 bg-red-500/10 text-red-400 rounded text-[10px] font-medium">
                              {t('limitReached')}
                            </span>
                          )}
                          {!isSandboxConv && isNearLimit && !isAtLimit && (
                            <span className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[10px] font-medium">
                              {t('almostFull')}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Delete button (non-select mode) */}
                    {!isSelecting && (
                      <button
                        onClick={(e) => handleDeleteSingle(conv.id, e)}
                        disabled={isDeleting}
                        className="flex-shrink-0 pr-4 py-4 pl-1 text-neutral-700 hover:text-red-400 transition-colors disabled:opacity-40"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </SheetContent>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null)
        }}
        title={
          confirmDelete?.type === 'bulk'
            ? `Delete ${selectedIds.size} conversation${selectedIds.size > 1 ? 's' : ''}?`
            : 'Delete conversation?'
        }
        description="This action cannot be undone. All messages in the selected conversation(s) will be permanently removed."
        confirmLabel="Delete"
        variant="danger"
        loading={isDeleting}
        onConfirm={executeDelete}
      />
    </Sheet>
  )
}
